import React, { useEffect, useRef, useState } from 'react';
import { ContextItem, ThreadContextItem } from '@varos/assistant-sdk';
import { IntegrationContainerBaseConfig } from '../../../base';
import { ThreadNewController } from './integrationNewInterface';
import { ThreadNewContainerProps, ThreadNewViewProps } from './integrationNewProps';
import { ThreadNewLoader } from './integrationNewInterface';
import { ThreadCommonViewProps } from '../common';
import { z } from 'zod';
import { ThreadNewUrlParamsSchema } from './integrationNewSchema';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { createChatStrategy } from '@varos/assistant-react-chat';
import { assert } from '@varos/util-typescript';
import { AssistantContext } from '@varos/assistant-react';
import { buildMessageContext } from '../detail/integrationDetailFactory';

export function createThreadNewContainer(
    config: IntegrationContainerBaseConfig,
    loader: ThreadNewLoader,
    controller: ThreadNewController,
    View: React.FC<ThreadCommonViewProps>
): React.FC<ThreadNewContainerProps> {
    const {
        kernel: {
            infra: {
                toaster: { useToast },
                optionParser: { useOptions },
            },
        },
        repository,
        context: { useContext },
        hook,
    } = config;

    return ({ ...containerProps }) => {
        const toast = useToast();
        const context = useContext();
        const navigate = useNavigate();
        const [searchParams, setSearchParams] = useSearchParams();
        const options = useOptions(ThreadNewUrlParamsSchema);
        const mutation = {
            thread: {
                create: repository.assistant.local.thread.useCreate(context),
            },
        };

        const [contextItems, setContextItems] = React.useState<ThreadContextItem[]>(
            () => {
                const items: ThreadContextItem[] = [];
                if (options.tool) {
                    items.push({
                        kind: 'solution',
                        solutionId: options.tool,
                    });
                }
                return items;
            }
        );

        const data = loader.useLoad(context, {
            integration: containerProps.integration,
            tool: options.tool
                ? {
                      id: options.tool,
                  }
                : null,
        });

        const appliedContextItems: ThreadContextItem[] = [
            ...contextItems,
            {
                kind: 'integration',
                integrationId: containerProps.integration.id,
            },
        ];

        const isAutoSubmitted = useRef(false);
        const threadCreatedRef = useRef(false);

        console.log('new props', data);

        useEffect(() => {
            async function autoSubmit() {
                if (
                    options.auto_submit &&
                    !isAutoSubmitted.current &&
                    !threadCreatedRef.current
                ) {
                    console.log('auto submitting initial message');
                    isAutoSubmitted.current = true;
                    // TODO improve url param handling this is not safe
                    setSearchParams(
                        (prev) => {
                            prev.delete('auto_submit');
                            prev.delete('tool');
                            return prev;
                        },
                        { replace: true }
                    );

                    // Create the thread directly with the initial message
                    try {
                        threadCreatedRef.current = true;
                        const title = `Chat with ${data.definition.display_name || 'Agent'}`;
                        console.log('auto-creating thread', title);
                        const response = await mutation.thread.create.mutateAsync({
                            title,
                            metadata: {
                                integrationId: containerProps.integration.id,
                            },
                            context: appliedContextItems,
                            messages: [
                                {
                                    role: 'user',
                                    parts: [],
                                    context: appliedContextItems.flatMap(
                                        (item): ContextItem[] => {
                                            if (item.kind === 'solution') {
                                                return [
                                                    {
                                                        kind: 'solution',
                                                        data: {
                                                            solutionId: item.solutionId,
                                                        },
                                                    },
                                                ];
                                            }
                                            if (item.kind === 'integration') {
                                                return [
                                                    {
                                                        kind: 'integration',
                                                        data: item,
                                                    },
                                                ];
                                            }
                                            console.warn('unknown context item', item);
                                            return [];
                                        }
                                    ),
                                },
                            ],
                        });
                        console.log('initial thread created', response);

                        // Navigate to thread detail view which will handle assistant response
                        navigate(
                            `/reporting/integrations/${containerProps.integration.id}`,
                            {
                                replace: true,
                            }
                        );
                    } catch (error) {
                        console.error('Error creating initial thread:', error);
                        threadCreatedRef.current = false;
                        toast({
                            kind: 'error',
                            description: 'Failed to create thread',
                        });
                    }
                }
            }
            autoSubmit();
        }, [
            options.auto_submit,
            data.definition.display_name,
            appliedContextItems,
            navigate,
        ]);

        const auth = hook.useAuth();
        assert(auth.scheme.kind === 'legacy', 'legacy auth scheme is required');
        assert(auth.scheme.store.authToken, 'auth token is required');

        const props = controller.useProps(context, data, {
            composer: {
                async onSubmit(params) {
                    try {
                        const title = `Chat with ${data.definition.display_name || 'Agent'}`;
                        const thread = await mutation.thread.create.mutateAsync({
                            title,
                            metadata: {
                                integrationId: containerProps.integration.id,
                            },
                            context: appliedContextItems,
                            messages: [
                                {
                                    role: 'user',
                                    parts: [
                                        {
                                            type: 'text',
                                            text: params.text,
                                        },
                                    ],
                                    context: appliedContextItems.flatMap(
                                        (item): ContextItem[] => {
                                            if (item.kind === 'integration') {
                                                return [
                                                    {
                                                        kind: 'integration',
                                                        data: item,
                                                    },
                                                ];
                                            }
                                            if (item.kind === 'solution') {
                                                return [
                                                    {
                                                        kind: 'solution',
                                                        data: item,
                                                    },
                                                ];
                                            }
                                            return [];
                                        }
                                    ),
                                },
                            ],
                        });
                        navigate(
                            `/reporting/integrations/${containerProps.integration.id}`,
                            {
                                replace: true,
                            }
                        );
                    } catch (error) {
                        console.error('Error sending message:', error);
                    }
                },
                placeholder: `Message ${data.definition.display_name || 'Agent'}...`,
                context: appliedContextItems.filter(
                    // TODO better handling of differentiation between public and hidden context
                    (item) => item.kind !== 'integration'
                ),
                onContextChange: setContextItems,
            },
        });
        return <View {...props.common} />;
    };
}
